import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { roleLevelKeys } from "@/lib/query";

/** One capability's expectations at one level, keyed by rating ordinal. */
export interface CapabilityExpectations {
  id: string;
  name: string;
  kind: "skill" | "competency";
  /**
   * The capability's own definition — what it IS, read before the per-rating
   * expectations. Null when it hasn't been written on the skill/competency.
   */
  description: string | null;
  /**
   * Rating ordinal → expectation text. A missing ordinal means nobody has
   * written that cell yet, which the UI says out loud rather than hiding.
   */
  byOrdinal: Record<number, string>;
}

/**
 * A role's expectation grid at one level. (Backend twin:
 * api/role-level-expectations.ts.)
 */
export interface LevelExpectations {
  roleLevelId: string;
  roleLevelName: string;
  /**
   * The rating rungs the viewer is allowed to see, ascending. **The server
   * decides this from the session** — admins get all five, everyone else gets
   * only the meets rung and the other four never cross the wire. So a single
   * entry here is the real access boundary, not a display choice.
   */
  ratings: { ordinal: number; name: string }[];
  /**
   * Which rung means "meets expectations". Shown alone by default even when all
   * five arrive — the rest are performance-management detail, revealed by the
   * grid's own toggle.
   */
  meetsOrdinal: number;
  capabilities: CapabilityExpectations[];
}

/**
 * The grid for one (role, level). Disabled until a level is chosen — the role
 * detail page picks one only after the applicable levels have loaded.
 */
export function useRoleLevelExpectations(roleId: string, roleLevelId: string | null) {
  return useQuery({
    queryKey: roleLevelKeys.expectations(roleId, roleLevelId ?? ""),
    enabled: roleId !== "" && roleLevelId !== null && roleLevelId !== "",
    queryFn: async (): Promise<LevelExpectations> => {
      const res = await fetch(`/api/roles/${roleId}/levels/${roleLevelId}/expectations`);
      if (!res.ok) throw new Error("Failed to load this level's expectations");
      const json = await res.json();
      return json.data;
    },
  });
}

/** One cell of the grid: what this capability looks like at this rating rung. */
export interface ExpectationCellInput {
  roleLevelId: string;
  kind: "skill" | "competency";
  capabilityId: string;
  ordinal: number;
  /** Blank or null clears the cell back to "not written yet". */
  expectation: string | null;
}

/**
 * Write cells of the grid (WI-111). Admin-only — the route enforces it.
 *
 * Takes an ARRAY because the editor saves a whole capability's rungs at once,
 * and the server's write is per cell. They're sent sequentially rather than in
 * parallel: each one opens a transaction and writes an audit row, and a failure
 * partway through should stop rather than race four more writes behind it.
 *
 * No success toast per cell — the caller toasts once for the save.
 */
export function useSetRoleLevelExpectations(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cells: ExpectationCellInput[]) => {
      for (const cell of cells) {
        const res = await fetch(`/api/roles/${roleId}/expectations`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cell),
        });
        if (!res.ok) {
          const body: unknown = await res.json().catch(() => null);
          const message =
            body !== null &&
            typeof body === "object" &&
            "error" in body &&
            typeof body.error === "string"
              ? body.error
              : "Failed to save the expectation";
          throw new Error(message);
        }
      }
    },
    onSuccess: () => {
      // Every level's grid shares the role's capabilities, so invalidate the
      // whole role-levels tree rather than guessing which keys moved.
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
    },
    // The server refuses a capability the role doesn't require — surface that
    // verbatim rather than paraphrasing it into something vaguer.
    onError: (e: Error) => toast.error(e.message || "Failed to save the expectation"),
  });
}
